import { DEFAULT_CUSTOM_ITEM_IMAGE } from '@apps/registry/common/components/Catalog/Catalog.constants';
import { Carousel } from '@apps/registry/common/components/Catalog/components/Carousel';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { addRendition } from '@shared/utils/photoRendition';
import { Box, IconV2, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { Content, Header, RecommendationsCarousel } from '../../../../../CatalogProductsIndex/components/RecommendationsCarousel';
import { CurationHeroHeaderCuration } from '../../CurationHeroHeader.types';
import { useCurationItems } from './CurationList.controller';
import { ITEM_SPACING, ItemButton, StyledContainer, StyledPhoto, styles } from './CurationList.styles';
import { ChevronRight } from '@withjoy/joykit/icons';

const OVERVIEW_PEEK_MOBILE_EXTRA_SIZE = 46;

export interface CurationListProps {
  curations: Array<CurationHeroHeaderCuration>;
  activeCurationId: string;
  isMobile: boolean;
  onCurationClick: (curationId: string, index: number) => void;
  onArrowClick?: (direction: 'left' | 'right') => void;
  onViewAllClick?: () => void;
  variant?: 'default' | 'overview';
  customTitle?: string;
}

const CurationList = React.forwardRef<HTMLDivElement, CurationListProps>(
  ({ curations, activeCurationId, isMobile, onArrowClick, onCurationClick, onViewAllClick, variant = 'default', customTitle }: CurationListProps, ref) => {
    const { t } = useTranslation('catalogRegistry');
    const curationList = t('curationList');
    const enablePeek = isMobile || variant !== 'overview';
    const peekMobileExtraSize = isMobile && variant === 'overview' ? OVERVIEW_PEEK_MOBILE_EXTRA_SIZE : 0;

    return (
      <StyledContainer
        __css={styles.container}
        ref={ref}
      >
        <RecommendationsCarousel>
          <Header
            enablePeek={enablePeek}
            onClick={isMobile ? onViewAllClick : undefined}
          >
            <Header.Start>
              <Box
                __css={styles.curationsTitle(variant)}
                fontWeight="semibold"
                lineHeight="1.3"
                letterSpacing="-.024em"
                onClick={onViewAllClick}
              >
                {customTitle ?? curationList.title()}
              </Box>
            </Header.Start>
            {onViewAllClick && (
              <Header.End>
                {isMobile ? (
                  <Box
                    as="button"
                    __css={styles.ctaArrow(variant)}
                    rowGap={6}
                  >
                    <IconV2
                      size="sm"
                      color="#333"
                    >
                      <ChevronRight />
                    </IconV2>
                  </Box>
                ) : (
                  <Box
                    __css={styles.curationsCta(variant)}
                    onClick={onViewAllClick}
                  >
                    {curationList.cta3({ total: `${curations.length}` })}
                  </Box>
                )}
              </Header.End>
            )}
          </Header>
          <Content style={{ overflowX: 'clip' }}>
            <Carousel
              totalNumberOfItems={curations.length}
              arrowMarginX={10}
              arrowOffsetY={-48}
              itemSpacing={24}
              enablePeek={enablePeek}
              peekMobileExtraSize={peekMobileExtraSize}
            >
              {curations.map((item, idx) => {
                return (
                  <Carousel.Item key={item.id}>
                    <CurationItem
                      curationData={item}
                      isActive={activeCurationId === item.id}
                      index={idx}
                      onCurationClick={onCurationClick}
                      variant={variant}
                    />
                  </Carousel.Item>
                );
              })}
            </Carousel>
          </Content>
        </RecommendationsCarousel>
      </StyledContainer>
    );
  },
);

interface CurationListItemProps {
  index: number;
  curationData: CurationHeroHeaderCuration;
  isActive: boolean;
  elementsPerPage?: Maybe<number>;
  onCurationClick: CurationListProps['onCurationClick'];
  variant?: 'default' | 'overview';
}

export const CurationItem = ({ curationData, isActive, index, onCurationClick, variant = 'default' }: CurationListItemProps) => {
  const { id } = curationData;
  const { title, eyebrow, backgroundImage } = curationData.header;

  return (
    <ItemButton
      as="button"
      onClick={() => onCurationClick(id, index)}
      __css={styles.itemButton}
      $isActive={isActive}
      $variant={variant}
    >
      <CurationPhoto
        photoUrl={backgroundImage?.url}
        isCoverImage={true}
      />
      <TextV2
        __css={styles.itemTitle(variant)}
        typographyVariant="label3"
      >
        {eyebrow}
      </TextV2>
      <TextV2
        __css={styles.itemDescription(variant)}
        typographyVariant={variant === 'overview' ? 'body1' : 'body3'}
      >
        {title}
      </TextV2>
    </ItemButton>
  );
};

interface CurationPhotoProps {
  isCoverImage?: boolean;
  photoUrl: Maybe<string>;
}

export const CurationPhoto: React.FC<CurationPhotoProps> = ({ photoUrl }) => {
  return (
    <StyledPhoto
      __css={styles.photo}
      _before={{
        backgroundImage: `url(${
          photoUrl
            ? addRendition({
                url: photoUrl,
                renditionSize: 'medium',
                isProduct: false,
              })
            : `{${DEFAULT_CUSTOM_ITEM_IMAGE}}`
        })`,
      }}
    />
  );
};

const CurationListSkeleton: React.FC = () => {
  const isMobile = useIsMobileScreen();
  const curationsLength = isMobile ? 3 : 6;
  const { elementsPerPage } = useCurationItems({ totalNumberOfItems: curationsLength, itemSpacing: ITEM_SPACING });

  return (
    <StyledContainer __css={styles.container}>
      <RecommendationsCarousel>
        <Header enablePeek={true}>
          <Header.Start>
            <SkeletonText
              rows={1}
              skeletonHeight={24}
              width={200}
            />
          </Header.Start>
          <Header.End>
            {isMobile ? (
              <SkeletonText
                rows={1}
                skeletonHeight={24}
                width={24}
              />
            ) : (
              <SkeletonText
                rows={1}
                skeletonHeight={24}
                width={200}
              />
            )}
          </Header.End>
        </Header>
        <Content style={{ overflowX: 'clip' }}>
          <Carousel
            totalNumberOfItems={elementsPerPage}
            itemSpacing={24}
            enablePeek={true}
            showSkeleton={true}
            placeholder={
              <Box __css={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                <SkeletonThumbnail
                  width="100%"
                  paddingTop={'56.25%'}
                  height="0"
                  borderRadius={8}
                  flexGrow={1}
                />
                <SkeletonText
                  rows={1}
                  skeletonHeight={5}
                  width="80%"
                  marginTop={3}
                />
                <SkeletonText
                  rows={1}
                  skeletonHeight={6}
                  width="90%"
                  marginTop={3}
                />
              </Box>
            }
          />
        </Content>
      </RecommendationsCarousel>
    </StyledContainer>
  );
};

export { CurationList, CurationListSkeleton };
